import { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.instation.app',
  headers: {
    'Content-Type': 'application/json'
  }
});

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      console.log('Initial user from localStorage:', savedUser);
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  });

  useEffect(() => {
    console.log('AuthContext user changed:', user);
    if (user?.token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${user.token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            // Add padding if needed
            const base64Url = tokenParts[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const padding = '='.repeat((4 - base64.length % 4) % 4);
            
            const decodedToken = JSON.parse(atob(base64 + padding));
            console.log('Auth context user data:', {
              user,
              decodedToken,
              userId: decodedToken?.userId
            });
          } else {
            console.warn('Invalid token format');
          }
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        // Don't let token decoding errors crash the app
      }
    }
  }, [user]);

  const login = async (userData) => {
    try {
      console.log('Raw userData received:', userData);
      
      // Extract is_new from the nested user object
      const is_new = userData.user?.is_new;
      console.log('is_new value:', is_new);

      // Store the complete user data, but don't override has_completed_onboarding
      const userToStore = {
        token: userData.token,
        ...userData.user,  // Spread the user data at top level
        is_new: is_new === 1,  // Convert MySQL 1/0 to boolean
        preferences: {
          ...userData.user?.preferences,
          // Remove hasCompletedOnboarding from local state since we'll use DB value
        }
      };
      
      console.log('Storing user data:', userToStore);
      setUser(userToStore);
      localStorage.setItem('user', JSON.stringify(userToStore));
      localStorage.setItem('token', userData.token);
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    console.log('AuthContext: Logging out');
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
  };

  const updateUser = (userData) => {
    console.log('Updating user data:', userData);
    try {
      // Update user state
      setUser(prevUser => ({ ...prevUser, ...userData }));
      
      // Update localStorage
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = { ...currentUser, ...userData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      return true;
    } catch (error) {
      console.error('UpdateUser error:', error);
      return false;
    }
  };

  const isTokenExpired = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return true;
      
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      return decoded.exp < currentTime;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true; // Assume expired if there's an error
    }
  };

  useEffect(() => {
    if (isTokenExpired()) {
      console.log('Token expired on startup, logging out user');
      logout();
      return;
    }

    const tokenCheckInterval = setInterval(() => {
      if (isTokenExpired()) {
        console.log('Token expired during session, logging out user');
        logout();
        alert('Your session has expired. Please log in again.');
        window.location.href = '/login';
      }
    }, 60000);

    return () => clearInterval(tokenCheckInterval);
  }, []);

  const value = {
    user,
    login,
    logout,
    updateUser,
    isTokenExpired
  };

  console.log('AuthContext rendering with value:', value);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 